import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Text,
    Textarea,
    Button,
    Checkbox
} from "@chakra-ui/react";
import { walletDialogPaperSx, walletTextFieldSx, walletCancelButtonSx } from "./dialogStyles";

/**
 * Calls the freeze/unfreeze API endpoint for a given user wallet.
 * @param {string} reason - The reason for the freeze/unfreeze action
 * @param {string} userId - The target user ID
 * @param {boolean} isFrozen - Whether the wallet is currently frozen
 * @param {Function} setWallet - State setter for wallet data
 * @param {Function} setReason - State setter to clear the reason field
 * @param {Function} setLoading - State setter for loading flag
 * @param {Function} onClose - Callback to close the dialog
 * @returns {Promise<void>}
 */
async function submitFreeze(reason, userId, isFrozen, setWallet, setReason, setLoading, onClose) {
    if (!reason.trim()) {
        toast.error("Please enter a reason");
        return;
    }
    setLoading(true);
    try {
        const response = await axios.put(`/admin/wallets/freeze/${userId}`, { reason: reason.trim() });
        setWallet(response.data?.wallet || response.data);
        toast.success(isFrozen ? "Wallet unfrozen successfully" : "Wallet frozen successfully");
        setReason('');
        onClose();
    } catch (error) {
        toast.error(error?.response?.data?.message || "Error during operation");
    } finally {
        setLoading(false);
    }
}

/**
 * Builds the confirm button style props based on frozen state.
 * @param {boolean} isFrozen - Whether the wallet is currently frozen
 * @returns {Object} Chakra style props for the confirm button
 */
function buildConfirmButtonProps(isFrozen) {
    return {
        background: isFrozen ? "rgba(76, 175, 80, 0.15)" : "rgba(255, 152, 0, 0.15)",
        color: isFrozen ? "#4caf50" : "#ff9800",
        border: `1px solid ${isFrozen ? "rgba(76, 175, 80, 0.4)" : "rgba(255, 152, 0, 0.4)"}`,
        fontWeight: 600,
        _hover: {
            background: isFrozen ? "rgba(76, 175, 80, 0.25)" : "rgba(255, 152, 0, 0.25)"
        },
        _disabled: { opacity: 0.5 }
    };
}

/**
 * Dialog component to freeze or unfreeze an artist wallet.
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback to close the dialog
 * @param {Object} props.wallet - Current wallet data
 * @param {string} props.userId - Target user ID for the API call
 * @param {Function} props.setWallet - State setter for wallet data
 * @returns {React.ReactElement}
 */
function FreezeDialog({ open, onClose, wallet, userId, setWallet }) {
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const isFrozen = wallet?.frozen;

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent {...walletDialogPaperSx}>
                <ModalHeader color="rgba(255, 255, 255, 0.95)" fontWeight={600}>
                    {isFrozen ? "Unfreeze wallet" : "Freeze wallet"}
                </ModalHeader>
                <FreezeDialogBody
                    isFrozen={isFrozen}
                    reason={reason}
                    setReason={setReason}
                />
                <FreezeDialogActions
                    reason={reason}
                    loading={loading}
                    isFrozen={isFrozen}
                    onCancel={onClose}
                    onConfirm={() => submitFreeze(reason, userId, isFrozen, setWallet, setReason, setLoading, onClose)}
                />
            </ModalContent>
        </Modal>
    );
}

/**
 * Renders the freeze dialog content with description text and reason input.
 * @param {Object} props
 * @param {boolean} props.isFrozen - Whether the wallet is currently frozen
 * @param {string} props.reason - Current reason text
 * @param {Function} props.setReason - State setter for the reason field
 * @returns {React.ReactElement}
 */
function FreezeDialogBody({ isFrozen, reason, setReason }) {
    const description = isFrozen
        ? "The wallet will be unfrozen and the user will be able to make transfers again."
        : "The wallet will be frozen and the user will no longer be able to make transfers or advances.";

    return (
        <ModalBody>
            <Text color="rgba(255, 255, 255, 0.7)" mb={4} fontSize="0.9rem">
                {description}
            </Text>
            <Textarea
                rows={3}
                placeholder="Reason (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                {...walletTextFieldSx}
            />
            {!isFrozen && (
                <Checkbox isChecked isDisabled mt={2} color="rgba(255,255,255,0.8)" colorScheme="teal">
                    Notifier l'utilisateur (automatique au gel)
                </Checkbox>
            )}
        </ModalBody>
    );
}

/**
 * Renders the freeze dialog action buttons (cancel and confirm).
 * @param {Object} props
 * @param {string} props.reason - Current reason text
 * @param {boolean} props.loading - Whether the action is processing
 * @param {boolean} props.isFrozen - Whether the wallet is currently frozen
 * @param {Function} props.onCancel - Cancel handler
 * @param {Function} props.onConfirm - Confirm handler
 * @returns {React.ReactElement}
 */
function FreezeDialogActions({ reason, loading, isFrozen, onCancel, onConfirm }) {
    return (
        <ModalFooter gap={2}>
            <Button onClick={onCancel} {...walletCancelButtonSx}>
                Cancel
            </Button>
            <Button
                onClick={onConfirm}
                isDisabled={loading || !reason.trim()}
                {...buildConfirmButtonProps(isFrozen)}
            >
                {loading ? "Processing..." : "Confirm"}
            </Button>
        </ModalFooter>
    );
}

export default FreezeDialog;
